<template>
  <v-dialog v-model="visible" max-width="440" persistent>
    <!-- Opt-in era (2026-09-11): the viewer has NO encryption identity at all
         (e.g. a report recipient who accepted the invite before setting up
         encryption). Nothing to unlock — send them to set it up. -->
    <v-card v-if="mode === 'setup'">
      <v-card-title class="text-h6 d-flex align-center">Set up End-to-End Encryption <encryption-gen-badge generation="new" /></v-card-title>
      <v-card-text>
        <!-- Cut to two lines 2026-10-08 (nobody read the long version). The
             one thing a viewer must take away: the OWNER has to sign in before
             anything shows — invite = grant (2026-09-17), no button for the
             owner to press, their next visit grants every recipient who holds
             an identity. -->
        <p class="mb-3">Enable the new encryption format to view this data.</p>
        <v-alert type="info" variant="tonal" density="compact" class="mb-0">
          Once you've set it up, <strong v-if="ownerEmail">{{ ownerEmail }}</strong><strong v-else>the account owner</strong> needs to sign in
          to their account before this data will show. Access is granted automatically when they do.
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="!!busy" @click="close">Not now</v-btn>
        <v-btn color="primary" class="text-white" :loading="busy === 'nav'" @click="goToSetup">Set up encryption</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Step one (user decision 2026-09-29): the passkey is by far the common
         case — same device, key cache gone — so it gets a card of its own with
         a single action, and every other way in waits behind it. The card
         never fires the browser's passkey sheet by itself: the dialog opens
         from a failed decrypt with no click behind it, and WebAuthn wants a
         user gesture. -->
    <v-card v-else-if="step === 'passkey'">
      <v-card-title class="text-h6 d-flex align-center">Unlock encrypted data <encryption-gen-badge generation="new" /></v-card-title>
      <v-card-text>
        <p class="mb-4">
          This browser doesn't hold the encryption key for
          <strong>{{ accountId }}</strong> yet. Unlock it with the passkey you created when you set up
          encryption. Your device will ask for your fingerprint, face or PIN.
        </p>

        <v-btn
          color="primary"
          block
          class="text-white"
          :loading="busy === 'passkey'"
          :disabled="!!busy"
          @click="unlockWithPasskey"
        >
          Unlock with passkey
        </v-btn>

        <!-- Cancel and "no passkey on this device" arrive as the same
             NotAllowedError, so this link is the only way to skip the
             browser's sheet on a device that never had the passkey (a
             Windows Hello passkey, viewed from a phone). -->
        <div class="text-center mt-2">
          <v-btn
            variant="text"
            size="small"
            :disabled="!!busy"
            @click="skipPasskey"
          >
            I don't have my passkey on this device
          </v-btn>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="!!busy" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Step two: the other ways in, with the passkey's failure on top when
         there was one. Also the FIRST card when the account has no passkey
         wrap (setup skipped it) or this browser has no WebAuthn (older
         WebViews) — then it reads like the plain unlock card. -->
    <v-card v-else>
      <v-card-title class="text-h6 d-flex align-center">{{ fallbackTitle }} <encryption-gen-badge generation="new" /></v-card-title>
      <v-card-text>
        <v-alert v-if="passkeyError" type="warning" variant="tonal" density="compact" class="mb-4">
          {{ passkeyError }}
        </v-alert>
        <p v-else-if="passkeyAvailable" class="mb-4">
          Here are the other ways to unlock the encryption key for
          <strong>{{ accountId }}</strong>:
        </p>
        <p v-else class="mb-4">
          This browser doesn't hold the encryption key for
          <strong>{{ accountId }}</strong> yet. Unlock it one of these ways:
        </p>

        <!-- Opened from the Link-a-device page (hideLinkDevice): that page IS
             this option, so don't offer a trip back to it. -->
        <template v-if="!hideLinkDevice">
          <v-btn
            variant="outlined"
            block
            class="mb-4"
            :disabled="!!busy"
            @click="goToLinkDevice"
          >
            Request keys from another device
          </v-btn>

          <v-divider class="mb-3" />
        </template>
        <p class="text-caption mb-2">{{ hideLinkDevice ? 'Use your saved backup code file:' : 'Or use your saved backup code file:' }}</p>
        <recovery-code-file-button
          :loading="busy === 'recovery' && !showManualRecovery"
          :disabled="!!busy"
          @code="onRecoveryCodeLoaded"
          @error="error = $event"
        />
        <div v-if="!showManualRecovery" class="text-center mt-1">
          <v-btn
            variant="text"
            size="small"
            :disabled="!!busy"
            @click="showManualRecovery = true"
          >
            Type the code instead
          </v-btn>
        </div>
        <template v-else>
          <v-text-field
            :model-value="recoveryCode"
            class="mt-3"
            density="compact"
            variant="outlined"
            placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
            autocomplete="off"
            autofocus
            :disabled="!!busy"
            @update:model-value="onRecoveryCodeInput"
            @keyup.enter="submitRecovery"
          />
          <v-btn
            variant="outlined"
            block
            :loading="busy === 'recovery'"
            :disabled="!!busy || !recoveryCode"
            @click="submitRecovery"
          >
            Unlock with typed code
          </v-btn>
        </template>

        <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mt-3">
          {{ error }}
        </v-alert>

        <!-- Finding ⑯ / OWNER-RESET.md: the way out for an account that lost
             every unlock method lives on the Account page — a viewer-only
             account simply starts over; an account with monitored devices
             starts over AND loses its encrypted history. -->
        <p class="text-caption text-medium-emphasis mt-4 mb-0">
          Lost your passkey and backup code? You can reset
          your account's encryption from your
          <a href="#" @click.prevent="goToAccount">Account page</a>. A reset deletes the encrypted
          screenshots and events from the last two weeks; nothing else is lost.
        </p>
      </v-card-text>
      <v-card-actions>
        <!-- A way back: some people cancel the sheet by accident or before
             their phone was ready, and the QR-code path from a phone is
             invisible to any "no passkey here" signal. -->
        <v-btn
          v-if="passkeyAvailable"
          variant="text"
          :loading="busy === 'passkey'"
          :disabled="!!busy"
          @click="unlockWithPasskey"
        >
          {{ passkeyError ? 'Try passkey again' : 'Use my passkey' }}
        </v-btn>
        <v-spacer />
        <v-btn variant="text" :disabled="!!busy" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  // NC1 (chunk 4) viewer-unlock dialog — the libsodium-scheme sibling of
  // E2EERecoveryPasswordRequest.vue (which stays Virgil-only). Opened via
  // Nc1UnlockPrompt.request(accountId); successful unlocks broadcast on the
  // shared E2EEPasscodePrompt.onUnlock channel so failed tiles retry.
  //
  // Two steps (user decision 2026-09-29, replacing one card that listed every
  // way in at once): the passkey first, alone; the other ways only after it
  // failed, was cancelled, or the user said they don't have it here. WebAuthn
  // has no silent "is one of this account's passkeys usable on this device?"
  // query, so the sequence is the only way to keep the recovery file and the
  // device link out of the common case's way.
  import { Nc1UnlockPrompt, passkeyUnlock, recoveryUnlock, passkeyUnlockAvailable, accountHasIdentity, announceUnlocked } from '@/services/e2ee/nc1/Unlock'
  import { resumePendingLink } from '@/services/e2ee/nc1/DeviceLink'
  import { Nc1PasskeyOffer } from '@/services/e2ee/nc1/PasskeyOffer'
  import E2EEPasscodePrompt, { sameAccount } from '@/services/e2ee/E2EEPasscodePrompt'
  import { maskRecoveryCodeField } from '@/services/e2ee/nc1/RecoveryCodeInput'
  import { dialogSafeNavigate } from '@/lib/dialogSafeNavigate'
  import RecoveryCodeFileButton from '@/components/e2ee/RecoveryCodeFileButton.vue'

  // Per account, for this page load only: the passkey step already failed, or
  // the user said they don't have the passkey on this device. The dialog
  // reopens on every further failed tile, and landing on the passkey card
  // again would feel like a loop — reopen on the fallback card instead, still
  // showing why. Value = the failure message (null when the user skipped).
  // A successful unlock clears the entry.
  const passkeySkipped = new Map()

  export default {
    name: 'Nc1UnlockDialog',
    components: { RecoveryCodeFileButton },
    data () {
      return {
        visible: false,
        mode: 'unlock',    // 'unlock' | 'setup' (no identity to unlock — opt-in era)
        step: 'passkey',   // 'passkey' | 'fallback' (unlock mode only)
        accountId: null,
        ownerEmail: null, // the viewed account, when it's someone else's — named on the set-up card
        passkeyAvailable: false,
        passkeyError: null, // why the passkey step failed — shown atop the fallback card
        hideLinkDevice: false, // opened from /link-device (see Nc1UnlockPrompt.request opts)
        unsubscribeUnlock: null,
        recoveryCode: '',
        showManualRecovery: false,
        busy: null,
        error: null,
      }
    },
    computed: {
      skipKey () {
        return String(this.accountId || '').toLowerCase()
      },
      // When the passkey step exists, the fallback card is "the other ways";
      // when it doesn't, the fallback card IS the unlock card.
      fallbackTitle () {
        return this.passkeyAvailable ? 'Other ways to unlock' : 'Unlock encrypted data'
      },
    },
    beforeMount () {
      Nc1UnlockPrompt.onRequest = (accountId, opts) => this.open(accountId, opts)
      // An unlock that happened elsewhere while this dialog was open — a link
      // request approved on another device and picked up by App.vue's watch
      // (2026-09-29) — leaves nothing to ask.
      this.unsubscribeUnlock = E2EEPasscodePrompt.onUnlock((accountId) => {
        if (this.visible && this.mode === 'unlock' && sameAccount(accountId, this.accountId)) this.finish()
      })
    },
    beforeUnmount () {
      Nc1UnlockPrompt.onRequest = null
      if (this.unsubscribeUnlock) this.unsubscribeUnlock()
    },
    methods: {
      async open (accountId, opts = {}) {
        this.accountId = accountId
        this.error = null
        this.passkeyError = null
        this.recoveryCode = ''
        this.showManualRecovery = false
        this.passkeyAvailable = false
        this.hideLinkDevice = !!opts.hideLinkDevice
        // The set-up card says whose sign-in completes the grant (2026-10-08):
        // the viewed account's owner, when that isn't the viewer themself.
        this.ownerEmail = opts.ownerEmail && !sameAccount(opts.ownerEmail, accountId) ? opts.ownerEmail : null
        // An explicit request that NAMES the passkey (the Link-a-device page's
        // button) starts on the passkey card even if it failed or was skipped
        // earlier in this page load — that memory is for the automatic prompts
        // from failed tiles, and the link page is usually reached FROM the
        // fallback card, so the memory is always set by then (user-reported
        // 2026-09-29).
        if (opts.restart) passkeySkipped.delete(this.skipKey)
        // A "Link a device" request this browser made earlier may have been
        // approved meanwhile (its one-time key survives in the keystore,
        // 2026-09-29): finish it instead of asking for anything.
        if ((await resumePendingLink({ email: accountId })) === 'granted') {
          announceUnlocked(accountId)
          Nc1UnlockPrompt.resolve(accountId)
          // The keys just arrived from another device: offer this browser
          // its own passkey (2026-09-30).
          Nc1PasskeyOffer.request(accountId, { reason: 'link' })
          return
        }
        // Encryption is opt-in (2026-09-11): the account may simply have no
        // identity yet, in which case every unlock option is a dead end —
        // show the set-up card instead. An unanswered probe (null) keeps the
        // unlock options rather than blocking a working browser.
        // Both probes run BEFORE the dialog shows, in parallel: which card
        // comes first depends on both, and deciding after opening flashed the
        // wrong card for the probe's round trip (user-reported 2026-09-11).
        // passkeyUnlockAvailable is false when the account has no passkey
        // wrap (setup skipped it) or this browser can't do WebAuthn.
        const [hasIdentity, passkeyAvailable] = await Promise.all([
          accountHasIdentity(accountId),
          passkeyUnlockAvailable(accountId),
        ])
        this.mode = hasIdentity === false ? 'setup' : 'unlock'
        this.passkeyAvailable = this.mode === 'unlock' && !!passkeyAvailable
        const skipped = passkeySkipped.has(this.skipKey)
        this.passkeyError = skipped ? passkeySkipped.get(this.skipKey) : null
        this.step = this.passkeyAvailable && !skipped ? 'passkey' : 'fallback'
        this.visible = true
      },
      // Shared by step one's primary button and step two's "Try passkey
      // again": any failure — cancelled, no usable passkey here, PRF missing,
      // offline — lands on the fallback card with the reason on top.
      async unlockWithPasskey () {
        this.busy = 'passkey'
        this.error = null
        this.passkeyError = null
        try {
          await passkeyUnlock(this.accountId)
          this.finish()
        } catch (e) {
          const message = e.message || 'Passkey unlock failed.'
          passkeySkipped.set(this.skipKey, message)
          this.passkeyError = message
          this.step = 'fallback'
        } finally {
          this.busy = null
        }
      },
      skipPasskey () {
        passkeySkipped.set(this.skipKey, null)
        this.passkeyError = null
        this.step = 'fallback'
      },
      onRecoveryCodeInput (value) {
        this.recoveryCode = maskRecoveryCodeField(value)
      },
      // A code found in the file is already validated — unlock straight away.
      onRecoveryCodeLoaded (code) {
        this.recoveryCode = code
        this.submitRecovery()
      },
      async submitRecovery () {
        this.busy = 'recovery'
        this.error = null
        try {
          await recoveryUnlock(this.accountId, this.recoveryCode)
          this.finish()
          // The recovery code got this browser in; offer it a passkey so the
          // code is not needed here next time (2026-09-30). Requested from
          // this success path — not from finish(), which the unlock
          // broadcast also reaches — because the broadcast does not say
          // which method unlocked. (The onUnlock listener above already ran
          // finish() during recoveryUnlock; finish() is idempotent.)
          Nc1PasskeyOffer.request(this.accountId, { reason: 'recovery' })
        } catch (e) {
          this.error = e.message || 'Recovery unlock failed.'
        } finally {
          this.busy = null
        }
      },
      // Every navigation out of this dialog goes through dialogSafeNavigate:
      // the dashboard dialog underneath (screenshot viewer, gallery, ...)
      // registers a guard that swallows the first push as "close me", which
      // made the Account-page link look like it went to the dashboard
      // (2026-09-11).
      async navigateTo (to) {
        this.busy = 'nav'
        try {
          this.close()
          const ok = await dialogSafeNavigate(this.$router, to)
          if (!ok) console.log('[nc1] navigation cancelled by an open dialog: ' + JSON.stringify(to))
        } finally {
          this.busy = null
        }
      },
      accountPath () {
        // Same in-app WebView workaround as HomeV2.openAccountPageForEnablingE2EE.
        return this.$store.state.inApp ? '/inAppAccountHack' : '/account'
      },
      goToLinkDevice () {
        this.navigateTo('/link-device')
      },
      goToAccount () {
        this.navigateTo(this.accountPath())
      },
      // ?forceEnableE2ee=nc1 auto-starts the NC1 opt-in on the Account page —
      // the scheme the account being viewed uses, so no chooser here.
      goToSetup () {
        this.navigateTo({ path: this.accountPath(), query: { forceEnableE2ee: 'nc1' } })
      },
      finish () {
        passkeySkipped.delete(this.skipKey)
        this.visible = false
        Nc1UnlockPrompt.resolve(this.accountId)
      },
      close () {
        this.visible = false
        // Explicit close without unlocking — let deferred dialogs resurface.
        Nc1UnlockPrompt.dismissed(this.accountId)
      },
    },
  }
</script>
